import { useMemo, useState } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { GetLocalStorage } from "../../../../../utils/functions/GetLocalStorage";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import { useGetAppraisalSummaryQuery } from "../../../../api/employeeApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import Pagination from "../../../Components/Pagination/Pagination";
import { ProDropFilter, SearchFilter } from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";

const SelfAppraisal = () => {
  const [yearFilter, setYearFilter] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const navigate = useNavigate();
  const user = GetLocalStorage();
  const {
    searchText,
    setSearchText,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const { data, isLoading } = useGetAppraisalSummaryQuery({
    id: user.staffId,
    SearchTerm: searchText,
    PageSize: pageSize,
    PageNumber: currentPage,
    Year: yearFilter,
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.result) return [];
    return [...data.result].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue;
      let bValue;

      if (sortColumn === "employee.fullName") {
        aValue = a.employee?.fullName;
        bValue = b.employee?.fullName;
      } else {
        aValue = a[sortColumn];
        bValue = b[sortColumn];
      }

      if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // For strings, make case-insensitive
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.result, sortColumn, sortDirection]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const currentYear = new Date().getFullYear();
  const dates = [
    ...Array.from({ length: currentYear - 1999 + 1 }, (_, i) => ({
      name: (currentYear - i).toString(),
      filter: (currentYear - i).toString(),
    })),
  ]; 
  return (
    <PageStyle hasBack={false} demoTitle={"Self Appraisal"} hasTopPageDemo>
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <ProDropFilter
          filter={yearFilter}
          setFilter={setYearFilter}
          name={"Year"}
          filterBy={dates}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th
                onClick={() => handleSort("summaryText")}
                style={{ cursor: "pointer" }}
              >
                Title {renderSortIcon("summaryText")}
              </th>
              <th
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer" }}
              >
                Created Date {renderSortIcon("createdDate")}
              </th>
              <th
                onClick={() => handleSort("employee.fullName")}
                style={{ cursor: "pointer" }}
              >
                Employee {renderSortIcon("employee.fullName")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {sortedData?.length > 0 &&
              sortedData?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item?.summaryText}</td>
                  <td>{FormatDateTime(item.createdDate, "DD/MM/YYYY")}</td>
                  <td>{item?.employee?.fullName}</td>
                  <td>
                    <TableActions
                      hasChildren={true}
                      url={`${item.referenceId}/view`}
                    >
                      {[
                        {
                          name: "View",
                          action: () => handleView(item.reviewId),
                        },
                      ]}
                    </TableActions>
                  </td>
                </tr>
              ))}
            {sortedData?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="text-center">
                  No Self Appraisal Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.totalPages}
        present_page={data?.currentPage}
        totalRows={data?.totalRows}
        pageSize={pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
    </PageStyle>
  );
};

export default SelfAppraisal;
